// try — the keeper recorder. Record over an optional drone, play whatever
// you like on any input path, Stop, hear it back, and see the soft checks.
// Checks are nudges, never verdicts: "Save keeper" lights up when they all
// pass, but "Keep it anyway" is ALWAYS offered when one doesn't — rules
// serve ears (core principle #2). Saving calls onKeeper(keeperId,
// keeperName, phrase) and unlocks Continue.
//
// Optional harmony bed (moment.bed), three shapes:
//   { prompt?, choices: [{ id, label, midis }] } — chips let the learner pick
//     which sustained note(s) hum under the drone while they record (Unit 2's
//     "two at once" keeper). The first choice starts selected; tapping a chip
//     previews it, or re-voices the bed live if it's already humming.
//   { chordDegree } — that chord's voicing (chordMidis) hums sustained under
//     the recording (Unit 5's melody-over-one-chord keepers).
//   { keeperId, times? } — the learner's OWN saved keeper (from the `keepers`
//     prop) loops underneath via playPhraseLoop (Unit 5's rewrite over the
//     chord loop). Falls back to the drone when the keeper is missing.
//     Unit 7: `withBeat: true` also loops the learner's beat keeper's drum
//     pattern (falls back to pulse) via startGroove under the same bed —
//     both stop together (stopBed / unmount).
// Like the drone, beds keep ringing through review/redo and stop on unmount.
//
// Unit 7 recording extras: `moment.recordPatch` names the voice the learner
// records with ('bassDrop' from the groove lenses, or any homeKeyAudio patch
// name); `moment.octaveShift` (semitones, e.g. -12 for bass) transposes what
// sounds and what's recorded while the on-screen piano keeps its own
// geography — the shift happens on the way into the voice map, and
// activeMidis shift back for display.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runChecks, chordMidis } from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playPhrase,
  playPhraseLoop,
  playNote,
  startNote,
  startDrone,
  resumeAudio,
} from "../../../lib/homeKeyAudio";
import { GROOVE_PATCHES, startGroove } from "../../../lib/homeKeyGroove";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

// Resolve moment.recordPatch by name: homeKeyAudio's PATCHES first, then the
// groove module's patch variants (that's where 'bassDrop' lives).
const resolveRecordPatch = (name) => {
  if (!name) return PATCHES.softKeys;
  return PATCHES[name] || GROOVE_PATCHES[name] || PATCHES.softKeys;
};

export default function TryMoment({ moment, onReadyChange, onKeeper, keepers }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | review | saved
  const [phrase, setPhrase] = useState(null);
  const [results, setResults] = useState(null);
  const [noteCount, setNoteCount] = useState(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const bed = moment.bed || null;
  const bedChoices = (bed && bed.choices) || [];
  // Unit 6: minor lessons drone on E — moment.droneMidi overrides the pitch
  // wherever this moment starts a drone (withDrone AND the bed fallback).
  const droneMidi = moment.droneMidi != null ? moment.droneMidi : 43;
  const [bedChoiceId, setBedChoiceId] = useState(() =>
    bedChoices.length ? bedChoices[0].id : null
  );

  const recState = useRef({ t0: 0, starts: new Map(), notes: [] });
  const droneRef = useRef(null);
  const bedRef = useRef(null); // held { stop } handles for the bed notes
  const playRef = useRef(null);
  const timersRef = useRef([]);

  const stopPlayback = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);
  const stopBed = useCallback(() => {
    if (bedRef.current) bedRef.current.forEach((h) => h.stop());
    bedRef.current = null;
  }, []);
  const stopAll = useCallback(() => {
    stopPlayback();
    stopBed();
    if (droneRef.current) droneRef.current.stop();
    droneRef.current = null;
  }, [stopPlayback, stopBed]);
  useEffect(() => () => stopAll(), [stopAll]);

  const startBed = (choiceId) => {
    if (!bed) return;
    // Keeper bed: the learner's own saved loop plays underneath. If they
    // haven't saved that keeper (or it has no phrase), fall back to the drone.
    if (bed.keeperId) {
      const keeper = (keepers || {})[bed.keeperId];
      const handles = [];
      if (keeper && keeper.phrase && (keeper.phrase.notes || []).length) {
        handles.push(
          playPhraseLoop(keeper.phrase, {
            times: bed.times || 4,
            patch: PATCHES.dreamPad,
            gain: 0.6,
          })
        );
      } else if (!droneRef.current) {
        droneRef.current = startDrone(droneMidi);
      }
      // withBeat: the learner's own drum pattern grooves under the loop.
      // beat is the full kit keeper; pulse (the kick-only keeper) fills in.
      if (bed.withBeat) {
        const beatKeeper = (keepers || {}).beat || (keepers || {}).pulse;
        if (beatKeeper && beatKeeper.pattern) {
          handles.push(
            startGroove({
              pattern: beatKeeper.pattern,
              bpm: bed.bpm || 92,
              swing: bed.swing || 0.5,
            })
          );
        }
      }
      if (handles.length) {
        stopBed();
        bedRef.current = handles;
      }
      return;
    }
    // Chord bed: sustain the chord's voicing under the recording.
    if (bed.chordDegree != null) {
      stopBed();
      bedRef.current = chordMidis(bed.chordDegree).map((m) =>
        startNote(PATCHES.dreamPad, m, 0.55)
      );
      return;
    }
    const choice =
      bedChoices.find((c) => c.id === choiceId) || bedChoices[0] || null;
    if (!choice) return;
    stopBed();
    bedRef.current = (choice.midis || []).map((m) =>
      startNote(PATCHES.dreamPad, m, 0.55)
    );
  };

  const pickBed = (choiceId) => {
    resumeAudio();
    setBedChoiceId(choiceId);
    if (bedRef.current) {
      startBed(choiceId); // already humming — re-voice it live
      return;
    }
    const choice = bedChoices.find((c) => c.id === choiceId);
    if (choice) (choice.midis || []).forEach((m) => playNote(PATCHES.dreamPad, m, 1400));
  };

  const ready = phase === "saved";
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  // Times are relative to the Record press, so leading silence is kept —
  // free-time recording, no quantization.
  const recorder = {
    onNoteStart: (midi, tMs) => {
      if (phaseRef.current !== "recording") return;
      recState.current.starts.set(midi, tMs);
    },
    onNoteEnd: (midi, tMs) => {
      if (phaseRef.current !== "recording") return;
      const r = recState.current;
      const start = r.starts.get(midi);
      if (start == null) return;
      r.starts.delete(midi);
      r.notes.push({
        midi,
        at: Math.max(0, Math.round(start - r.t0)),
        dur: Math.max(40, Math.round(tMs - start)),
      });
      setNoteCount(r.notes.length);
    },
  };

  const recordPatch = useMemo(
    () => resolveRecordPatch(moment.recordPatch),
    [moment.recordPatch]
  );
  const octaveShift = moment.octaveShift || 0;

  const voices = useHomeKeyVoices(recordPatch, recorder);
  const { press, release, activeMidis } = voices;
  // Shift on the way in (sound + recording), shift back for the key lights.
  const pressShifted = useCallback(
    (midi) => press(midi + octaveShift),
    [press, octaveShift]
  );
  const releaseShifted = useCallback(
    (midi) => release(midi + octaveShift),
    [release, octaveShift]
  );
  const displayMidis = useMemo(() => {
    if (!octaveShift) return activeMidis;
    const shifted = new Set();
    activeMidis.forEach((m) => shifted.add(m - octaveShift));
    return shifted;
  }, [activeMidis, octaveShift]);
  const enabled = phase !== "saved";
  useTypingKeys(pressShifted, releaseShifted, enabled);
  const midi = useHomeKeyMidi(pressShifted, releaseShifted, enabled);

  const startRecording = () => {
    resumeAudio();
    stopPlayback();
    if (moment.withDrone && !droneRef.current) {
      droneRef.current = startDrone(droneMidi);
    }
    if (bed) startBed(bedChoiceId);
    recState.current = { t0: performance.now(), starts: new Map(), notes: [] };
    setNoteCount(0);
    setPhrase(null);
    setResults(null);
    setPhase("recording");
  };

  const stopRecording = () => {
    voices.releaseAll(); // closes (and records) anything still held
    const notes = [...recState.current.notes].sort((a, b) => a.at - b.at);
    const captured = { notes };
    setPhrase(captured);
    setResults(runChecks(moment.checks || [], captured));
    setPhase("review");
    if (notes.length) {
      timersRef.current.push(
        setTimeout(() => {
          playRef.current = playPhrase(captured, recordPatch);
        }, 400)
      );
    }
  };

  const replay = () => {
    if (!phrase || !phrase.notes.length) return;
    resumeAudio();
    stopPlayback();
    playRef.current = playPhrase(phrase, recordPatch);
  };

  const redo = () => {
    stopPlayback();
    setPhrase(null);
    setResults(null);
    setNoteCount(0);
    setPhase("idle"); // the drone keeps humming if it's on — just go again
  };

  const save = () => {
    if (!phrase || !phrase.notes.length) return;
    onKeeper(moment.keeperId, moment.keeperName, phrase);
    setPhase("saved");
  };

  const hasNotes = phrase && phrase.notes.length > 0;
  const allPass = results != null && results.every((r) => r.pass);

  return (
    <div className="hk-moment-body hk-try-panel">
      <p>{moment.instructions}</p>

      {bed && phase !== "saved" && (
        <>
          {bed.prompt && <p className="hk-echo-status">{bed.prompt}</p>}
          {bedChoices.length > 0 && (
            <div className="hk-choice-row" role="group" aria-label="what hums underneath?">
              {bedChoices.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  className={`hk-choice${
                    bedChoiceId === choice.id ? " hk-choice--picked" : ""
                  }`}
                  aria-pressed={bedChoiceId === choice.id}
                  onClick={() => pickBed(choice.id)}
                >
                  {choice.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {(phase === "idle" || phase === "recording") && (
        <div className="hk-choice-row">
          {phase === "idle" ? (
            <button type="button" className="hk-btn hk-btn--primary" onClick={startRecording}>
              ● record{moment.withDrone ? " (the drone comes with you)" : ""}
            </button>
          ) : (
            <>
              <span className="hk-echo-status" aria-live="polite">
                recording…
                {noteCount > 0 && ` ${noteCount} note${noteCount === 1 ? "" : "s"} so far`}
              </span>
              <button type="button" className="hk-btn hk-btn--primary" onClick={stopRecording}>
                ◼ stop
              </button>
            </>
          )}
        </div>
      )}

      {phase === "review" && (
        <>
          {hasNotes ? (
            <>
              <div className="hk-choice-row">
                <button type="button" className="hk-demo-btn" onClick={replay}>
                  ▶ hear it back
                </button>
              </div>
              {results && (
                <div className="hk-checks">
                  {results.map((r) => (
                    <div
                      key={r.id}
                      className={`hk-check-note${r.pass ? " hk-check-note--pass" : ""}`}
                    >
                      {r.pass ? "✓ " : "~ "}
                      {r.message}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="hk-echo-status">
              silence is a bold artistic statement, but this keeper wants notes — roll again.
            </p>
          )}
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              disabled={!hasNotes || !allPass}
              onClick={save}
            >
              save keeper
            </button>
            <button type="button" className="hk-btn hk-btn--ghost" onClick={redo}>
              redo
            </button>
            {hasNotes && !allPass && (
              <button type="button" className="hk-btn hk-btn--ghost" onClick={save}>
                actually, I like it — keep it anyway
              </button>
            )}
          </div>
        </>
      )}

      {phase === "saved" && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of the music now.
        </p>
      )}

      <Piano
        activeMidis={displayMidis}
        onPress={pressShifted}
        onRelease={releaseShifted}
        disabled={!enabled}
      />
      {midi.connected && (
        <p className="hk-midi-note">MIDI connected: {midi.deviceNames.join(", ")}</p>
      )}
    </div>
  );
}
